import { type FieldBlockInfo } from '@lighthouse/block'
import type {
    FieldADTValue,
    PageStackBlockValueBlockMap,
    PageStackBlockValueItem,
    RecordLikeProtocol,
    VariableADTvalue
} from '@lighthouse/core'
import { type InputValueItem, VariableType } from '@lighthouse/core'
import { getEmptyCellValue, getFieldInputInitialValue, isEmptyCellValue, useApplicationContext, useAtomData } from '@lighthouse/shared'
import { find } from 'rambda'
import { useCallback } from 'react'

import { appRoleAtom } from '@/atoms/application/state'

import { useCurrentAppId, useCurrentEnvId } from './useApplication'
import { useDataSourceList } from './useDataSource'
import { useParseVariable2Field4SelectDataSource, useParseVariableWithSubscription } from './useParseVariable'

export function useFieldBlockDefaultValueRender(initialValue?: VariableADTvalue, record?: RecordLikeProtocol) {
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const userId = useAtomData(appRoleAtom)
    const dataSourceList = useDataSourceList(appId, envId)

    const parseVariable2Field = useParseVariableWithSubscription(initialValue)
    const parseSelectDataSourceVariable2Field = useParseVariable2Field4SelectDataSource(initialValue)
    // const recordRef = useLatest(record)

    return useCallback(
        async (fieldBlockInfo: FieldBlockInfo, fieldBlockValueMap?: PageStackBlockValueBlockMap): Promise<PageStackBlockValueItem> => {
            const {
                fieldId,
                inputType,
                id,
                dsId,
                config,
                config: { noRepeat: isCheckRepeat }
            } = fieldBlockInfo
            const { schema } = dataSourceList.find(item => item.id === dsId) ?? {}
            const field = schema?.[fieldId]
            const user = find(user => user.id === userId, personOptions)
            const recordValue = record && field ? ({ value: record.content?.[fieldId]?.value, ...field } as FieldADTValue) : undefined
            const initialValue = config.initialValue
                ? config.initialValue.type === VariableType.SELECT_DATASOURCE
                    ? await parseSelectDataSourceVariable2Field(config.initialValue, {
                          parseVariable2Field,
                          pageStackFormState: fieldBlockValueMap
                      })
                    : parseVariable2Field(config.initialValue, {
                          timeZone: config.inputType === 'calendar' ? config.calendar?.timeZone : undefined
                      })
                : undefined
            const finallyInitialValue =
                initialValue && initialValue.innerType !== 'NULL' ? ({ ...initialValue, ...field } as FieldADTValue) : undefined
            const emptyValue = { ...field, value: getEmptyCellValue(field?.type ?? 'text') } as FieldADTValue

            const defaultInputValue = getFieldInputInitialValue({
                fieldValue:
                    (finallyInitialValue && finallyInitialValue.innerType !== 'NULL' && !isEmptyCellValue(finallyInitialValue)
                        ? finallyInitialValue
                        : recordValue) ?? emptyValue,
                config,
                fieldType: field?.type ?? 'text',
                currentUser: user,
                personOptions,
                roleOptions,
                departmentOptions
            })

            return {
                value: defaultInputValue,
                type: inputType,
                blockType: 'field',
                fieldType: field?.type,
                isCheckRepeat,
                id,
                fieldId
            } as PageStackBlockValueItem
        },
        [
            record,
            dataSourceList,
            departmentOptions,
            parseSelectDataSourceVariable2Field,
            parseVariable2Field,
            personOptions,
            roleOptions,
            userId
        ]
    )
}
